import React, {
  FC,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
} from 'react';
import './SignInComponent.css';
import { Formik, FormikProps } from 'formik';
import * as Yup from 'yup';
import { useNavigate } from 'react-router';
import { SignInType } from '../utils/type';
import { loginUser } from '../services/userApi/User';
import MediumTypography from '../components/formlib/MediumTypography';
import SnackBarComponent from '../components/formlib/SnackBarComponent';
import { Context } from '../LanguageWrapper';
import {
  SSO_REDIRECT_URI,
  STORAGE_USERFIRSTNAME_KEY,
  STORAGE_USERLASTNAME_KEY,
  STORAGE_USERNAME_KEY,
  STORAGE_USER_ID_KEY,
  VERSION,
} from '../services/Constant';
import ButtonComponent from '../components/formlib/ButtonComponent';
import { AlertColor } from '@mui/material';
import { ReactComponent as TopLeftLogo } from '../assets/images/ShapeLeftTop.svg';
import { ReactComponent as TopRightLogo } from '../assets/images/Shape-TopRight.svg';
import { ReactComponent as BottomLeftLogo } from '../assets/images/Shape-BottomLeft.svg';
import { ReactComponent as BottomRightLogo } from '../assets/images/Shape-BottomRight.svg';
import { ReactComponent as ThomLogo } from '../assets/images/ThomConnectLogo.svg';
import { ReactComponent as ThomConnectBottomLogo } from '../assets/images/ThomConnectBottomLogo.svg';
import { Box } from '@mui/system';
import { useIntl } from 'react-intl';
import dayjs from 'dayjs';
import { ApiError, isCustomError } from '../services/ApiResponseHandler';
import { deleteAll, initDB } from '../services/storageDB';
import { UnSavedChangesContext } from '../context/UnSavedChangesProvider';
import { useClearCache } from 'react-clear-cache';

const initialValues: SignInType = {
  email: '',
  password: '',
};

export interface LoginBody {
  username: string;
  password: string;
  grantType: string;
  appSource: string;
}

interface SignInPropType {
  onLoginSuccess: (userId: string) => void;
}

const SignInComponent: FC<SignInPropType> = ({ onLoginSuccess }) => {
  const [open, setOpen] = React.useState(false);
  const [loader, setLoader] = React.useState(false);
  const [toastrVariable, setToastrVariable] =
    React.useState<AlertColor>('info');
  const [toastrDefaultMessage, setToastrDefaultMessage] = React.useState('');
  const [toastrId, setToastrId] = React.useState<string>();
  const intl = useIntl();
  const { isLatestVersion, emptyCacheStorage } = useClearCache();

  useLayoutEffect(() => {
    if (!isLatestVersion) {
      emptyCacheStorage();
    }
  }, []);

  useEffect(() => {
    initDB()
      .then(() => {
        console.log('DB initialized to delete data');
        deleteAll();
      })
      .catch((error) => {
        console.log('DB initialized to delete data', error);
      });
  }, []);

  const getCopyRightMessage = () => {
    return `© ${dayjs().year()} ${intl.formatMessage({
      id: 'copyrightMessage',
      defaultMessage: 'Thom Child and Family Services All Rights Reserved',
    })}`;
  };

  const handleClose = (
    event?: React.SyntheticEvent | Event,
    reason?: string,
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    setToastrId(undefined);
    setOpen(false);
  };

  const submit = async (values: SignInType) => {
    setLoader(true);
    const userparams: LoginBody = {
      username: values.email,
      password: values.password,
      grantType: 'password',
      appSource: 'TCS',
    };

    loginUser(userparams)
      .then(async (response) => {
        setOpen(true);
        // clearing for permission denied flag
        const webAppBuildNumber = localStorage.getItem('webAppBuildNumber');
        localStorage.clear();
        if (webAppBuildNumber) {
          localStorage.setItem('webAppBuildNumber', webAppBuildNumber);
        }
        // clearing for permission denied flag
        if (response.programList === null) {
          setToastrVariable('error');
          setToastrId('forbiddenErrorMessage');
          setToastrDefaultMessage('Unable to login, Please contact admin');
        } else {
          localStorage.setItem('accessToken', response.accessToken);
          localStorage.setItem('refreshToken', response.refreshToken);
          localStorage.setItem('offlineToken', response.offlineToken);
          localStorage.setItem(STORAGE_USERNAME_KEY, response.name);
          localStorage.setItem(STORAGE_USERFIRSTNAME_KEY, response.firstName);
          localStorage.setItem(STORAGE_USERLASTNAME_KEY, response.lastName);
          localStorage.setItem(STORAGE_USER_ID_KEY, response.userId);
          localStorage.setItem('refreshTokenExpiry', response.refreshExpiresIn);
          localStorage.setItem('superProgramId', response.superProgramId);
          localStorage.setItem(
            'defaultProgramId',
            response.defaultProgramId ?? '',
          );
          context.updatePrograms(response.programList);
          context.updateSelectedProgram(
            response.defaultProgramId
              ? response.programList.find(
                  (program) => program.id === response.defaultProgramId,
                ) ?? response.programList[0]
              : response.programList[0],
            false,
          );
          navigation('/');
          onLoginSuccess(response.userId);
        }
      })
      .catch((error) => {
        setOpen(true);
        setLoader(false);
        setToastrVariable('error');
        if (isCustomError(error)) {
          const apiError = error as ApiError;
          setToastrId(apiError.id);
          setToastrDefaultMessage(apiError.message);
        } else {
          setToastrId('Signin.errortoastr');
          setToastrDefaultMessage('Invalid username or password');
        }
      })
      .finally(() => {
        setLoader(false);
      });
  };

  const navigation = useNavigate();

  const validationSchema = Yup.object({
    email: Yup.string()
      .email('Invalid Email format')
      .required('EmailRequired.Validation'),
    password: Yup.string().required('SignIn.validation.passwordRequired'),
  });

  const formRef = useRef<FormikProps<SignInType>>(null);
  const handleFormSubmit = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      formRef.current?.submitForm();
    }
  };

  const context = useContext(Context);
  const { handleUpdateUnsavedChanges } = useContext(UnSavedChangesContext);
  useEffect(() => {
    context.selectLanguage();
    navigation('/');
  }, []);

  useEffect(() => {
    handleUpdateUnsavedChanges(false);
  }, []);

  const handleVisibilityChange = useCallback(() => {
    if (
      document.visibilityState === 'visible' &&
      localStorage.getItem(STORAGE_USERNAME_KEY) !== null
    ) {
      globalThis.location.reload();
    }
  }, []);

  useEffect(() => {
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  return (
    <>
      {toastrId && (
        <SnackBarComponent
          open={open}
          handleClose={handleClose}
          successOrError={toastrVariable}
          labelId={toastrId}
          defaultMessageId={toastrDefaultMessage}
        />
      )}
      <Box className="loginContainerStyle3">
        <TopLeftLogo className="topLeftCornerImage" />
        <TopRightLogo className="topRightCornerImage" />
        <BottomLeftLogo className="bottomLeftCornerImage" />
        <BottomRightLogo className="bottomRightCornerImage" />
        <Box className="cardView" onKeyDown={handleFormSubmit}>
          <Box style={{ justifyContent: 'center', display: 'flex' }}>
            <ThomLogo />
          </Box>
          <Formik
            initialValues={initialValues}
            validateOnMount={true}
            innerRef={formRef}
            enableReinitialize={true}
            onSubmit={(values) => submit(values)}
            validationSchema={validationSchema}
          >
            {() => (
              <Box style={{ marginTop: '5%' }}>
                <ButtonComponent
                  className="btn-primary btn-submitLogin login_button"
                  type="submit"
                  labelId="SignIn.WithSSO"
                  defaultLabelId="Login with SSO"
                  onClick={() => {
                    globalThis.location.href = `https://login.microsoftonline.com/ae724e48-68ed-4412-8951-0be3b4fe70d0/oauth2/v2.0/authorize?client_id=159e6442-3309-461a-b6d2-3ac3e377f9c9&response_type=code&redirect_uri=${SSO_REDIRECT_URI}auth&response_mode=query&scope=openid%20profile%20offline_access`;
                  }}
                  loading={loader}
                />
              </Box>
            )}
          </Formik>
          <Box className="reservedText">
            <ThomConnectBottomLogo
              style={{ cursor: 'pointer' }}
              onClick={() => globalThis.open('https://www.thomchild.org/')}
            />
            <Box className="justifyContent-Center">
              <MediumTypography
                label={getCopyRightMessage()}
                textColor="white"
                marginTop="8px"
                fontSize="12px"
              />
            </Box>
            <Box className="justifyContent-Center">
              <MediumTypography
                className="pl-md"
                label={`Version - ${VERSION}`}
                textColor="white"
                marginTop="-5px"
                fontSize="12px"
              />
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default SignInComponent;
